import { supabase } from '../../lib/supabase'
import type { WeekdayDefault } from '../agent/scheduleDefaults'

/**
 * docs/product/scheduling-model.md's "Schedule defaults": what an employee
 * normally works on each weekday, or that the weekday is a regular day off,
 * held in muster.employee_schedule_defaults
 * (20260925040000_employee_schedule_defaults.sql). A row exists only for a
 * weekday a supervisor has set; every other weekday falls back to the
 * employee's shift template (its own weekdays worked, the rest off), so
 * "rebuild from shift template" is deleting the employee's rows. Written
 * straight from the browser under row-level security, each change an audit
 * event.
 *
 * A change applies from the next unpublished pay period: it never rewrites a
 * period's own already-real day-by-day schedule. It only changes what the
 * profile shows as the employee's normal week going forward.
 */
export type ScheduleDefaultShift = { name: string; time: string; appliesTo: string }

export type EmployeeScheduleDefault = {
  shift: ScheduleDefaultShift | null
  // Monday first, all seven days, whether set by a supervisor or the
  // shift template's own.
  weekly: WeekdayDefault[]
}

// Postgres's own extract(dow from ...): 0 is Sunday.
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0]

type ShiftTemplateRow = { name: string; start_time: string; end_time: string; weekdays: number[] }

type PlacementRow = { shift_templates: ShiftTemplateRow | null }

type DefaultRow = {
  weekday: number
  status_codes: { category: string } | null
  assignments: { name: string } | null
}

function clock(time: string): string {
  return time.slice(0, 5).replace(':', '')
}

// "Monday–Friday" from a template's own weekdays, or a comma list where they
// are not one unbroken run.
function appliesTo(weekdays: number[]): string {
  const days = MONDAY_FIRST.filter((day) => weekdays.includes(day))
  if (days.length === 0) return 'No days'
  const contiguous = days.every((day, index) => index === 0 || MONDAY_FIRST.indexOf(day) === MONDAY_FIRST.indexOf(days[index - 1]) + 1)
  return contiguous && days.length > 1
    ? `${WEEKDAY_NAMES[days[0]]}–${WEEKDAY_NAMES[days[days.length - 1]]}`
    : days.map((day) => WEEKDAY_NAMES[day]).join(', ')
}

async function currentShift(employeeId: string): Promise<ShiftTemplateRow | null> {
  const today = new Date()
  const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  const { data } = await supabase
    .from('employee_assignments')
    .select('shift_templates(name, start_time, end_time, weekdays)')
    .eq('employee_id', employeeId)
    .eq('active', true)
    .lte('effective_from', iso)
    .or(`effective_to.is.null,effective_to.gte.${iso}`)
  const placement = ((data ?? []) as unknown as PlacementRow[])[0]
  return placement?.shift_templates ?? null
}

// One employee's week: the shift template's own, with each weekday a
// supervisor has set laid over it.
export async function getScheduleDefault(employeeId: string): Promise<EmployeeScheduleDefault> {
  const [shift, { data }] = await Promise.all([
    currentShift(employeeId),
    supabase
      .from('employee_schedule_defaults')
      .select('weekday, status_codes(category), assignments(name)')
      .eq('employee_id', employeeId),
  ])
  const set = new Map(((data ?? []) as unknown as DefaultRow[]).map((row) => [row.weekday, row]))

  const weekly = MONDAY_FIRST.map((day): WeekdayDefault => {
    const row = set.get(day)
    if (row) {
      return row.status_codes?.category === 'work'
        ? { weekday: WEEKDAY_NAMES[day], category: 'work', assignment: row.assignments?.name }
        : { weekday: WEEKDAY_NAMES[day], category: 'off' }
    }
    return shift?.weekdays.includes(day)
      ? { weekday: WEEKDAY_NAMES[day], category: 'work' }
      : { weekday: WEEKDAY_NAMES[day], category: 'off' }
  })

  return {
    shift: shift ? { name: shift.name, time: `${clock(shift.start_time)}–${clock(shift.end_time)}`, appliesTo: appliesTo(shift.weekdays) } : null,
    weekly,
  }
}

// The signed-in agent's own week: their employee record is the one whose
// profile is theirs (employees_read_own), and their own defaults are
// readable to them (employee_schedule_defaults_read_own). An account with no
// employee record has none.
export async function getMyScheduleDefault(userId: string): Promise<EmployeeScheduleDefault | null> {
  const { data } = await supabase.from('employees').select('id').eq('profile_id', userId).maybeSingle()
  return data ? getScheduleDefault(data.id) : null
}

export type ScheduleDefaultChange = {
  weekday: string
  category: 'work' | 'off'
  // The assignment a work day carries, by its catalog name; none for a plain
  // scheduled workday or a day off.
  assignment?: string
}

// Sets one weekday of an employee's default. Returns whether the write
// landed, the same as every other write in this workspace.
export async function setScheduleDefaultWeekday(userId: string, employeeId: string, change: ScheduleDefaultChange): Promise<boolean> {
  const weekday = WEEKDAY_NAMES.indexOf(change.weekday)
  if (weekday === -1) return false

  const statusName = change.category === 'work' ? 'Scheduled workday' : 'Regular day off'
  const [status, assignment, shift] = await Promise.all([
    supabase.from('status_codes').select('id').eq('name', statusName).maybeSingle(),
    change.assignment
      ? supabase.from('assignments').select('id').eq('name', change.assignment).maybeSingle()
      : Promise.resolve({ data: null }),
    change.category === 'work' ? currentShift(employeeId) : Promise.resolve(null),
  ])
  if (!status.data) return false
  if (change.assignment && !assignment.data) return false

  const { error } = await supabase.from('employee_schedule_defaults').upsert(
    {
      employee_id: employeeId,
      weekday,
      segment: 1,
      status_code_id: status.data.id,
      assignment_id: assignment.data?.id ?? null,
      start_time: shift ? shift.start_time : null,
      end_time: shift ? shift.end_time : null,
      created_by: userId,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'employee_id,weekday,segment' },
  )
  return !error
}

// "Rebuild from shift template": clears every weekday a supervisor has set,
// so the week reads as the shift template's own again.
export async function rebuildScheduleDefault(employeeId: string): Promise<boolean> {
  const { error } = await supabase.from('employee_schedule_defaults').delete().eq('employee_id', employeeId)
  return !error
}
